import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import { FormControl, Grid, InputLabel, MenuItem, Select, TextField } from '@mui/material';
import PropTypes from 'prop-types';

import { AUSTRALIA, IRELAND } from '../../../../constants/constants';
import { getDeployRegion } from '../../../../utils/configurationHelpers';

const MedicareField = (props) => {
  const {
    values,
    errors,
    helperText,
    label,
    required,
    disabled,
    t,
    staticWords,
    disableUnderline,
    handleOnBlur,
    handleOnChange,
  } = props;

  const existedValues = { existedMedNumber: '', existedMedicare_ref_no: '' };

  if (values?.national_id === '-') {
    existedValues.existedMedNumber = '-';
    existedValues.existedMedicare_ref_no = '-';
  } else if (values?.national_id?.length) {
    const lastCharacter = values.national_id.slice(-1); // Gets the last character
    const pendingCharacters = values.national_id.slice(0, -1); // Gets all except the last character
    existedValues.existedMedNumber = pendingCharacters;
    existedValues.existedMedicare_ref_no = lastCharacter;
  }

  return (
    <Grid container spacing={1}>
      <Grid item xs={disableUnderline ? 6 : 9}>
        <TextField
          fullWidth
          label={label}
          name="medicare_number"
          value={values.medicare_number || existedValues.existedMedNumber}
          error={errors.medicare_number?.show && errors?.medicare_number?.error}
          required={required}
          helperText={
            errors.medicare_number?.show && errors.medicare_number?.error
              ? t(errors?.medicare_number?.error)
              : helperText
          }
          inputProps={{
            style: { textTransform: 'uppercase' },
          }}
          InputProps={{ disableUnderline: disableUnderline }}
          disabled={disabled}
          autoComplete="off"
          onBlur={handleOnBlur}
          onChange={handleOnChange}
        />
      </Grid>
      {!(disableUnderline && values?.national_id === '-') && (
        <Grid item xs={disableUnderline ? 6 : 3}>
          <FormControl fullWidth disabled={disabled} required={required}>
            <InputLabel
              id="medicare-reference-number"
              sx={{ color: (theme) => errors.medicare_ref_no?.show && theme.palette.error.main }}
            >
              {disableUnderline ? ' ' : staticWords.Ref_No_}
            </InputLabel>
            <Select
              name="medicare_ref_no"
              labelId="medicare-reference-number"
              id="medicare-reference-number"
              value={values.medicare_ref_no || existedValues.existedMedicare_ref_no}
              disableUnderline={disableUnderline}
              IconComponent={disableUnderline && null}
              renderValue={(value) => {
                if (!value || value === '0') {
                  return '-';
                }

                return value;
              }}
              onBlur={handleOnBlur}
              onChange={handleOnChange}
            >
              {Array.from({ length: 10 }, (_, i) => {
                return (
                  <MenuItem value={i === 0 ? '0' : i.toString()} key={i}>
                    {i === 0 ? '-' : i}
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>
        </Grid>
      )}
    </Grid>
  );
};

const DefaultField = (props) => {
  const {
    values,
    helperText,
    errors,
    label,
    required,
    disabled,
    disableUnderline,
    t,
    staticWords,
    handleOnBlur,
    handleOnChange,
  } = props;

  return (
    <Grid container>
      <TextField
        label={label}
        name="national_id"
        value={values.national_id}
        fullWidth
        required={required}
        error={errors.national_id.show && !!errors.national_id.error}
        helperText={
          errors.national_id.show && errors.national_id.error
            ? t(errors.national_id.error)
            : disabled
              ? ' '
              : helperText
        }
        inputProps={{
          style: { textTransform: 'uppercase' },
        }}
        disabled={disabled}
        autoComplete="off"
        InputProps={{ disableUnderline: disableUnderline }}
        onBlur={handleOnBlur}
        onChange={handleOnChange}
      />
    </Grid>
  );
};

const NationalIDField = (props) => {
  const {
    country,
    label,
    required,
    helperText,
    disabled,
    onChange,
    onError,
    pattern,
    excludedChars,
    maxLength,
    name,
    initialValue = '',
    disableUnderline,
    InputProps = {},
    resetValues,
  } = props;

  const { t } = useTranslation();
  const staticWords = useSelector((state) => state.handlingTranslation.words);

  const parseValue = (value) => {
    switch (country) {
      case AUSTRALIA: {
        const medicareNumber = value.slice(0, 10);
        const medicareRefNo = value.slice(10, 11);

        return {
          medicare_number: medicareNumber,
          medicare_ref_no: medicareRefNo,
        };
      }
      default:
        return { national_id: initialValue };
    }
  };

  const getInitialError = () => {
    switch (country) {
      case AUSTRALIA:
        return {
          medicare_number: {
            error: '',
            show: false,
          },
          medicare_ref_no: {
            error: '',
            show: false,
          },
        };
      default:
        return {
          national_id: {
            error: '',
            show: false,
          },
        };
    }
  };

  const [values, setValues] = useState(parseValue(initialValue));
  const [errors, setErrors] = useState(getInitialError());
  const [output, setOutput] = useState(initialValue);

  useEffect(() => {
    if (initialValue === '-') {
      setValues(parseValue('-'));
    } else if (!initialValue) {
      setValues(parseValue(''));
    } else if (initialValue !== output) {
      setValues(parseValue(initialValue));
      setErrors(getInitialError());
    }
  }, [initialValue]);

  useEffect(() => {
    if (!resetValues) setErrors(getInitialError());
  }, [resetValues]);

  const handleOnChange = (event) => {
    const { name, value } = event.target;

    let output = value;

    const newValues = { ...values, [name]: output };
    if (getDeployRegion() === IRELAND) {
      let value = value.replace(/\D/g, ''); // Remove non-numeric characters
      // Format the input as 999-999-9999
      if (value.length > 3 && value.length <= 6) {
        value = `${value.slice(0, 3)}-${value.slice(3)}`;
      } else if (value.length > 6) {
        value = `${value.slice(0, 3)}-${value.slice(3, 6)}-${value.slice(6, 13)}`;
      }
      newValues.national_id = value;
    }
    const isValid = validateField(name, newValues);
    onError?.(!isValid);

    setValues(newValues);
    if (onChange) {
      if (name === 'medicare_number') {
        if (values.medicare_ref_no === undefined) {
          output = output + '';
        } else {
          output = output + values.medicare_ref_no;
        }
      } else if (name === 'medicare_ref_no') {
        if (!output || output === '0') {
          output = values.medicare_number + '';
        } else {
          output = values.medicare_number + output;
        }
      }
      onChange({ target: { name: name, value: output } });
    }
    setOutput(output);
  };

  const validateMedicareField = (values) => {
    const { medicare_number, medicare_ref_no } = values;
    let isValid = true;
    let newErrors = { ...errors };

    // Checks if medicare number is valid and is not empty
    if (medicare_number?.length > 0 || medicare_ref_no > 0) {
      if (!pattern.test(medicare_number)) {
        newErrors = {
          ...newErrors,
          medicare_number: {
            ...errors['medicare_number'],
            error: 'Invalid_medicare_number',
          },
        };
        isValid = false;
      } else {
        newErrors = {
          ...newErrors,
          medicare_number: {
            ...errors['medicare_number'],
            error: '',
          },
        };
      }

      if (!/^[1-9]/.test(medicare_ref_no)) {
        newErrors = {
          ...newErrors,
          medicare_ref_no: {
            ...errors['medicare_ref_no'],
            error: 'Invalid_reference_number',
          },
        };
        isValid = false;
      } else {
        newErrors = {
          ...newErrors,
          medicare_ref_no: {
            ...errors['medicare_ref_no'],
            error: '',
          },
        };
      }
    }

    setErrors(newErrors);
    return isValid;
  };

  const validateField = (fieldName, values) => {
    // validate Medicare fields
    if (['medicare_number', 'medicare_ref_no'].includes(fieldName)) {
      return validateMedicareField(values);
    }
    // if field is not required, check if it is empty
    if (values?.[fieldName] && values[fieldName]?.length > 0) {
      // if pattern is provided, validate against it

      if (pattern) {
        const regex = new RegExp(pattern);
        if (
          getDeployRegion() === IRELAND &&
          fieldName === 'national_id' &&
          values['national_id'].length > 0 &&
          values['national_id'].length > 12
        ) {
          setErrors({
            ...errors,
            [fieldName]: {
              ...errors[fieldName],
              error: '',
            },
          });
          return true;
        } else {
          if (!regex.test(values?.[fieldName])) {
            setErrors({
              ...errors,
              [fieldName]: {
                ...errors?.[fieldName],
                error: 'Invalid_Input',
              },
            });
            return false;
          }
        }
      }
    }
    setErrors({
      ...errors,
      [fieldName]: {
        ...errors?.[fieldName],
        error: '',
      },
    });
    return true;
  };

  const handleOnBlur = () => {
    const newErrors = Object.fromEntries(
      Object.entries(errors).map(([key, value]) => {
        return [key, { ...value, show: !!value.error }];
      })
    );
    setErrors(newErrors);
  };

  switch (country) {
    case AUSTRALIA:
      return (
        <MedicareField
          values={values}
          errors={errors}
          helperText={helperText}
          label={label}
          required={required}
          disabled={disabled}
          t={t}
          staticWords={staticWords}
          disableUnderline={disableUnderline}
          handleOnBlur={handleOnBlur}
          handleOnChange={handleOnChange}
        />
      );
    default:
      return (
        <DefaultField
          values={values}
          errors={errors}
          helperText={helperText}
          label={label}
          required={required}
          disabled={disabled}
          t={t}
          staticWords={staticWords}
          disableUnderline={disableUnderline}
          handleOnBlur={handleOnBlur}
          handleOnChange={handleOnChange}
        />
      );
  }
};

NationalIDField.propTypes = {
  country: PropTypes.string,
  label: PropTypes.string,
  required: PropTypes.bool,
  helperText: PropTypes.string,
  disabled: PropTypes.bool,
  onChange: PropTypes.func,
  onError: PropTypes.func,
  pattern: PropTypes.any,
  excludedChars: PropTypes.any,
  maxLength: PropTypes.number,
  name: PropTypes.string,
  initialValue: PropTypes.string,
  InputProps: PropTypes.object,
};

export default NationalIDField;
